import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked, ViewEncapsulation, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { xParseLocalizedNumber } from '@phoenix/common';
import { S_DLI_CSV_IMPORT_DEFAULT_QUANTITY_ONE } from '../../../../@core/constants/settings-keys';
import { UiLocaleService } from '../../../../@core/services/i18n/ui-locale.service';
import { createSettingsSignal } from '../../../../@core/services/settings.service';
import { ServerConfigService } from '../../../../data/server-config';
import { CustomFieldConfig, Document, SaveLineItemOptions, SaveLineItemResult, StockAction } from '../../../../generated/generated-types';
import { BaseCsvImportDirective, CsvImportShellComponent, MappingOption } from '../../../../shared/components/csv-import';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { DocumentLineItemService } from '../../services/document-line-item.service';
import { DocumentLineItemDisplayClass } from '../widget-add-edit-document-line-item/types-interfaces';

type DocumentLineItemWithValidation = DocumentLineItemDisplayClass & {
  importIsValid: WritableSignal<boolean | undefined>;
  importError: WritableSignal<string | undefined>;
  importWarning: WritableSignal<string | undefined>;
  importProductExists: WritableSignal<boolean | undefined>;
  originalCsvRow?: string[];
  isValidating: WritableSignal<boolean>;
};

/** Matches {@link SaveLineItemResultCode.RESERVATION_DEFERRED_TO_DEMAND} until codegen is run. */
const RESERVATION_DEFERRED_TO_DEMAND = 'RESERVATION_DEFERRED_TO_DEMAND';

export interface DocumentLineItemCsvImportResult {
  items: DocumentLineItemWithValidation[];
  deferredCount: number;
}

@Component({
  selector: 'app-widget-import-dli-from-file',
  imports: [
    FormsModule,
    TranslatePipe,
    PhxCheckboxComponent,
    CsvImportShellComponent,
  ],
  standalone: true,
  templateUrl: './widget-import-dli-from-file.component.html',
  styleUrl: './widget-import-dli-from-file.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetImportDliFromFileComponent
  extends BaseCsvImportDirective<DocumentLineItemWithValidation, DocumentLineItemCsvImportResult> {

  private readonly uiLocale = inject(UiLocaleService);
  private readonly documentLineItemService = inject(DocumentLineItemService);
  private readonly serverConfigService = inject(ServerConfigService);

  document: Document;

  deferredImportCount = signal<number>(0);
  importDefaultQuantityOneSettingSig = createSettingsSignal<boolean>(S_DLI_CSV_IMPORT_DEFAULT_QUANTITY_ONE, true);
  hasQuantityColumnMapped = computed(() => this.columnMappings().includes('quantity'));
  customFields = signal<CustomFieldConfig[]>([]);

  constructor() {
    super();
    void this.initMappingOptions();

    effect(() => {
      this.importDefaultQuantityOneSettingSig();
      const noQuantityColumn = !this.hasQuantityColumnMapped();
      if (!noQuantityColumn) {
        return;
      }
      untracked(() => {
        if (this.csvData().length > 0 && this.hasRequiredMappings()) {
          this.generatePreviewData();
          if (this.itemsToImport().length > 0) {
            setTimeout(() => this.validateItemsAutomatically(), 300);
          }
        }
      });
    });
  }

  getMappingOptions(): MappingOption[] {
    const standardOptions: MappingOption[] = [
      { label: this.i18n.translate(_('entities.DocumentLineItem.productIdentifier')), value: 'productIdentifier', icon: 'fa fa-barcode' },
      { label: this.i18n.translate(_('entities.Product.gtin')), value: 'gtin', icon: 'fa fa-qrcode' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.quantity')), value: 'quantity', icon: 'fa fa-hashtag' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.requestedQuantity')), value: 'requestedQuantity', icon: 'fa fa-hashtag' },
      { label: this.i18n.translate(_('entities.StoragePlace.identifier')), value: 'storagePlaceIdentifier', icon: 'fa fa-warehouse' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.description')), value: 'description', icon: 'fa fa-edit' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.itemPrice')), value: 'itemPrice', icon: 'fa fa-dollar' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.discountPercentage')), value: 'discountPercentage', icon: 'fa fa-percent' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.positionText')), value: 'positionText', icon: 'fa fa-align-left' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.internalNotes')), value: 'internalNotes', icon: 'fa fa-comment' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.orderNumber')), value: 'orderNumber', icon: 'fa fa-hashtag' },
      { label: this.i18n.translate(_('entities.DocumentLineItem.deliveryDate')), value: 'deliveryDate', icon: 'fa fa-calendar' },
    ];

    try {
      const customFieldConfigs = this.serverConfigService.getCustomFieldsFor('DocumentLineItem');
      this.customFields.set(customFieldConfigs);

      const customFieldOptions: MappingOption[] = customFieldConfigs.map(field => ({
        label: this.getCustomFieldLabel(field),
        value: `customFields.${field.name}`,
        icon: this.getCustomFieldIcon(field.type),
        isCustomField: true,
        customFieldType: field.type,
        styleClass: 'italic',
      }));

      return [...standardOptions, ...customFieldOptions];
    } catch (error) {
      console.error('Error loading custom fields:', error);
      return standardOptions;
    }
  }

  hasRequiredMappings(): boolean {
    return this.columnMappings().includes('productIdentifier') || this.columnMappings().includes('gtin');
  }

  protected override getCsvLlmEntityName(): string {
    return 'DocumentLineItem';
  }

  protected override getCsvLlmRequiredTargetKeys(): string[] {
    return ['productIdentifier', 'gtin'];
  }

  protected override getCsvLlmMappingRules(): string[] {
    return [
      ...super.getCsvLlmMappingRules(),
      'At least one of productIdentifier or gtin must be mapped.',
      'Custom fields use targetKey customFields.<name> as listed in the field table.',
    ];
  }

  suggestMappingForHeader(normalizedHeader: string): string | null {
    if (normalizedHeader.includes('product') ||
      normalizedHeader.includes('sku') ||
      normalizedHeader.includes('identifier') ||
      normalizedHeader.includes('artikelnummer') ||
      normalizedHeader.includes('artikel') ||
      normalizedHeader.includes('artikel-nummer') ||
      normalizedHeader.includes('artikel_nummer') ||
      normalizedHeader.includes('produktnummer') ||
      normalizedHeader.includes('produkt-nummer') ||
      normalizedHeader.includes('produkt_nummer')) {
      return 'productIdentifier';
    }
    if (normalizedHeader.includes('gtin') ||
      normalizedHeader.includes('ean') ||
      normalizedHeader.includes('barcode') ||
      normalizedHeader.includes('upc') ||
      normalizedHeader.includes('strichcode') ||
      normalizedHeader.includes('ean-code') ||
      normalizedHeader.includes('ean_code') ||
      normalizedHeader.includes('gtin-code') ||
      normalizedHeader.includes('gtin_code')) {
      return 'gtin';
    }
    if (normalizedHeader.includes('requestedquantity') ||
      normalizedHeader.includes('requested_quantity') ||
      normalizedHeader.includes('requested-quantity') ||
      normalizedHeader.includes('bedarfsmenge') ||
      normalizedHeader.includes('bedarf') ||
      normalizedHeader.includes('demand')) {
      return 'requestedQuantity';
    }
    if (normalizedHeader.includes('quantity') ||
      normalizedHeader.includes('qty') ||
      normalizedHeader.includes('amount') ||
      normalizedHeader.includes('menge') ||
      normalizedHeader.includes('anzahl') ||
      normalizedHeader.includes('stück') ||
      normalizedHeader.includes('stueck')) {
      return 'quantity';
    }
    if (normalizedHeader.includes('storage') ||
      normalizedHeader.includes('warehouse') ||
      normalizedHeader.includes('location') ||
      normalizedHeader.includes('lager') ||
      normalizedHeader.includes('lagerplatz') ||
      normalizedHeader.includes('lager-platz') ||
      normalizedHeader.includes('lager_platz') ||
      normalizedHeader.includes('standort')) {
      return 'storagePlaceIdentifier';
    }
    if (normalizedHeader.includes('description') ||
      normalizedHeader.includes('name') ||
      normalizedHeader.includes('title') ||
      normalizedHeader.includes('beschreibung') ||
      normalizedHeader.includes('bezeichnung') ||
      normalizedHeader.includes('titel')) {
      return 'description';
    }
    if (normalizedHeader.includes('price') ||
      normalizedHeader.includes('cost') ||
      normalizedHeader.includes('preis') ||
      normalizedHeader.includes('kosten') ||
      normalizedHeader.includes('einzelpreis') ||
      normalizedHeader.includes('einzel-preis') ||
      normalizedHeader.includes('einzel_preis')) {
      return 'itemPrice';
    }
    if (normalizedHeader.includes('discount') ||
      normalizedHeader.includes('rabatt') ||
      normalizedHeader.includes('nachlass') ||
      normalizedHeader.includes('skonto')) {
      return 'discountPercentage';
    }
    return this.detectCustomFieldMapping(normalizedHeader);
  }

  mapCsvRow(row: string[], _rowIndex: number): DocumentLineItemWithValidation | null {
    const item: Partial<DocumentLineItemWithValidation> = {
      documentId: this.document?.id,
      ...this.createRowBase(row),
      importProductExists: signal(undefined),
    };

    this.columnMappings().forEach((mapping, colIndex) => {
      if (mapping && row[colIndex]) {
        const value = row[colIndex].trim();
        if (!value) {
          return;
        }

        switch (mapping) {
          case 'productIdentifier':
            item.productIdentifier = value;
            break;
          case 'gtin':
            break;
          case 'quantity':
            item.quantity = xParseLocalizedNumber(value) || 0;
            break;
          case 'requestedQuantity':
            item.requestedQuantity = xParseLocalizedNumber(value) || 0;
            break;
          case 'storagePlaceIdentifier':
            (item as DocumentLineItemWithValidation & { storagePlaceIdentifier?: string }).storagePlaceIdentifier = value;
            break;
          case 'description':
            item.description = value;
            break;
          case 'itemPrice':
            item.itemPrice = xParseLocalizedNumber(value) || 0;
            break;
          case 'discountPercentage':
            item.discountPercentage = xParseLocalizedNumber(value) || 0;
            break;
          case 'positionText':
            item.positionText = value;
            break;
          case 'internalNotes':
            item.internalNotes = value;
            break;
          default:
            if (mapping.startsWith('customFields.')) {
              const fieldName = mapping.substring('customFields.'.length);
              this.setCustomFieldValue(item, fieldName, value);
            }
            break;
        }
      }
    });

    this.applyDefaultQuantityIfNeeded(item);

    return item as DocumentLineItemWithValidation;
  }

  async validateBatch(items: DocumentLineItemWithValidation[], abortSignal: AbortSignal): Promise<void> {
    try {
      const validationInputs = items.map(item => {
        const input: Record<string, unknown> = {
          documentId: this.document?.id,
          productIdentifier: item.productIdentifier,
          gtin: this.getGtinFromItem(item),
          quantity: item.quantity,
          requestedQuantity: item.requestedQuantity,
          storagePlaceIdentifier: (item as DocumentLineItemWithValidation & { storagePlaceIdentifier?: string }).storagePlaceIdentifier,
          description: item.description,
          itemPrice: item.itemPrice,
          discountPercentage: item.discountPercentage,
          positionText: item.positionText,
          internalNotes: item.internalNotes,
          orderNumber: (item as DocumentLineItemWithValidation & { orderNumber?: string }).orderNumber,
          deliveryDate: (item as DocumentLineItemWithValidation & { deliveryDate?: string }).deliveryDate,
        };

        const customFields = this.customFields();
        customFields.forEach(field => {
          const value = (item as any)[`customFields.${field.name}`];
          if (value !== undefined && value !== null) {
            if (!input.customFields) {
              input.customFields = {};
            }
            (input.customFields as Record<string, unknown>)[field.name] = value;
          }
        });

        return input;
      });

      const validationResult = await this.documentLineItemService.validateDocumentLineItemsForImport(validationInputs as any);

      if (abortSignal.aborted) {
        return;
      }

      if (validationResult?.validateDocumentLineItemsForImport) {
        const results = validationResult.validateDocumentLineItemsForImport;

        results.forEach((result, index) => {
          if (index < items.length) {
            const item = items[index];

            if (result.valid) {
              item.importIsValid.set(true);
              item.importError.set(undefined);
              item.importProductExists.set(true);
              if (result.documentLineItem?.productId) {
                item.productId = result.documentLineItem.productId;
              }
            } else {
              item.importIsValid.set(false);
              item.importError.set(
                result.validationMessage || this.t('validation-failed')
              );
              item.importProductExists.set(false);
            }
            item.isValidating.set(false);
          }
        });
      } else {
        await Promise.all(items.map(item => this.validateSingleItemFallback(item, abortSignal)));
      }
    } catch (error) {
      if (!abortSignal.aborted) {
        console.error('Error validating batch:', error);
        await Promise.all(items.map(item => this.validateSingleItemFallback(item, abortSignal)));
      }
    }
  }

  private static readonly IMPORT_BATCH_SIZE = 10;

  async importValidItems(validItemsToImport: DocumentLineItemWithValidation[]): Promise<DocumentLineItemCsvImportResult> {
    const preparedItems = validItemsToImport.map(item => {
      const preparedItem = { ...item };

      const customFields = this.customFields();
      if (customFields.length > 0) {
        const customFieldsData: Record<string, unknown> = {};
        customFields.forEach(field => {
          const value = (item as any)[`customFields.${field.name}`];
          if (value !== undefined && value !== null) {
            customFieldsData[field.name] = value;
          }
        });

        if (Object.keys(customFieldsData).length > 0) {
          (preparedItem as any).customFields = customFieldsData;
        }
      }

      return preparedItem;
    });

    const saveOptions: SaveLineItemOptions | undefined = this.isReserveDocument()
      ? { saveWithDeferredReservation: true } as SaveLineItemOptions
      : undefined;

    // Batch saves so the shared shell progress bar can advance.
    const saveResults: SaveLineItemResult[] = [];
    const batchSize = WidgetImportDliFromFileComponent.IMPORT_BATCH_SIZE;
    for (let i = 0; i < preparedItems.length; i += batchSize) {
      const batch = preparedItems.slice(i, i + batchSize);
      const batchResults = await this.documentLineItemService.saveItemsEx(batch, saveOptions ?? {});
      saveResults.push(...batchResults);
      this.reportImportProgress(Math.min(i + batch.length, preparedItems.length));
    }

    let deferredCount = 0;
    saveResults.forEach((result, index) => {
      const item = validItemsToImport[index];
      if (!item || !result?.resultCodes?.some(code => code === RESERVATION_DEFERRED_TO_DEMAND)) {
        return;
      }
      deferredCount++;
      item.importWarning.set(this.i18n.translate(
        _('document-line-item.csv-import.reservation-deferred-warning'),
        {
          quantity: result.item?.quantity ?? 0,
          requestedQuantity: result.item?.requestedQuantity ?? item.requestedQuantity ?? item.quantity ?? 0,
        },
      ));
    });

    const importResult: DocumentLineItemCsvImportResult = { items: preparedItems, deferredCount };

    if (deferredCount > 0) {
      this.deferredImportCount.set(deferredCount);
      this.importFinished.set(true);
    }

    return importResult;
  }

  getMappedCellDisplay(item: DocumentLineItemWithValidation, mapping: string, columnIndex: number): string {
    switch (mapping) {
      case 'productIdentifier':
        return item.productIdentifier || '';
      case 'gtin':
        return this.getGtinFromItem(item) || '';
      case 'quantity':
        return item.quantity ? item.quantity.toString() : '';
      case 'requestedQuantity':
        return item.requestedQuantity ? item.requestedQuantity.toString() : '';
      case 'storagePlaceIdentifier':
        return (item as DocumentLineItemWithValidation & { storagePlaceIdentifier?: string }).storagePlaceIdentifier || '';
      case 'description':
        return item.description || '';
      case 'itemPrice':
        return item.itemPrice ? item.itemPrice.toString() : '';
      case 'discountPercentage':
        return item.discountPercentage ? item.discountPercentage.toString() : '';
      case 'positionText':
        return item.positionText || '';
      case 'internalNotes':
        return item.internalNotes || '';
      case 'orderNumber':
        return (item as DocumentLineItemWithValidation & { orderNumber?: string }).orderNumber || '';
      case 'deliveryDate':
        return (item as DocumentLineItemWithValidation & { deliveryDate?: string }).deliveryDate || '';
      default:
        if (mapping.startsWith('customFields.')) {
          const fieldName = mapping.substring('customFields.'.length);
          return this.getCustomFieldValue(item, fieldName);
        }
        return item.originalCsvRow?.[columnIndex] || '';
    }
  }

  override validateItemsAutomatically(): void {
    this.deferredImportCount.set(0);
    super.validateItemsAutomatically();
  }

  protected override resetItemValidationExtras(item: DocumentLineItemWithValidation): void {
    item.importProductExists.set(undefined);
  }

  protected override isPrimaryMapping(mapping: string): boolean {
    return mapping === 'productIdentifier' || mapping === 'gtin';
  }

  protected override buildFinishResult(): DocumentLineItemCsvImportResult {
    return {
      items: this.itemsToImport().filter(item => item.importIsValid() || item.importWarning?.()),
      deferredCount: this.deferredImportCount(),
    };
  }

  private getCustomFieldLabel(field: CustomFieldConfig): string {
    const lang = this.uiLocale.language;
    const localizedLabel = field.label?.find(x => x.languageCode === lang)?.value;

    if (localizedLabel) {
      return localizedLabel;
    }

    const translationKey = `columns.customFields.${field.name}`;
    const translation = this.i18n.translate(translationKey);

    if (translation && translation !== translationKey) {
      return translation;
    }

    return field.name;
  }

  private getCustomFieldIcon(fieldType: string): string {
    switch (fieldType) {
      case 'string':
        return 'fa fa-text-width';
      case 'int':
      case 'number':
      case 'float':
        return 'fa fa-hashtag';
      case 'boolean':
        return 'fa fa-check-square';
      case 'datetime':
        return 'fa fa-calendar';
      case 'select':
        return 'fa fa-list';
      case 'customentity':
      case 'customEntity':
        return 'fa fa-link';
      default:
        return 'fa fa-asterisk';
    }
  }

  private detectCustomFieldMapping(normalizedHeader: string): string | null {
    const customFields = this.customFields();

    for (const field of customFields) {
      const fieldName = field.name.toLowerCase();
      const fieldLabel = this.getCustomFieldLabel(field).toLowerCase();
      const normalizedFieldName = fieldName.replace(/[\s\-_]/g, '');
      const normalizedFieldLabel = fieldLabel.replace(/[\s\-_]/g, '');
      const normalizedHeaderClean = normalizedHeader.replace(/[\s\-_]/g, '');

      if (normalizedHeader === fieldName ||
        normalizedHeader === fieldLabel ||
        normalizedHeaderClean === normalizedFieldName ||
        normalizedHeaderClean === normalizedFieldLabel ||
        normalizedHeader.includes(fieldName) ||
        normalizedHeader.includes(fieldLabel) ||
        normalizedHeaderClean.includes(normalizedFieldName) ||
        normalizedHeaderClean.includes(normalizedFieldLabel)) {
        return `customFields.${field.name}`;
      }

      switch (field.type) {
        case 'string':
          if (this.matchesStringFieldPatterns(normalizedHeader, fieldName) ||
            this.matchesStringFieldPatterns(normalizedHeader, fieldLabel)) {
            return `customFields.${field.name}`;
          }
          break;
        case 'int':
        case 'number':
        case 'float':
          if (this.matchesNumberFieldPatterns(normalizedHeader, fieldName) ||
            this.matchesNumberFieldPatterns(normalizedHeader, fieldLabel)) {
            return `customFields.${field.name}`;
          }
          break;
        case 'boolean':
          if (this.matchesBooleanFieldPatterns(normalizedHeader, fieldName) ||
            this.matchesBooleanFieldPatterns(normalizedHeader, fieldLabel)) {
            return `customFields.${field.name}`;
          }
          break;
        case 'datetime':
          if (this.matchesDateFieldPatterns(normalizedHeader, fieldName) ||
            this.matchesDateFieldPatterns(normalizedHeader, fieldLabel)) {
            return `customFields.${field.name}`;
          }
          break;
      }
    }

    return null;
  }

  private matchesStringFieldPatterns(header: string, fieldName: string): boolean {
    const normalizedFieldName = fieldName.replace(/[\s\-_]/g, '').toLowerCase();
    const normalizedHeader = header.replace(/[\s\-_]/g, '').toLowerCase();

    return (header.includes('text') && header.includes(fieldName)) ||
      (header.includes('string') && header.includes(fieldName)) ||
      (header.includes('custom') && header.includes(fieldName)) ||
      normalizedHeader.includes(normalizedFieldName);
  }

  private matchesNumberFieldPatterns(header: string, fieldName: string): boolean {
    const normalizedFieldName = fieldName.replace(/[\s\-_]/g, '').toLowerCase();
    const normalizedHeader = header.replace(/[\s\-_]/g, '').toLowerCase();

    return (header.includes('number') && header.includes(fieldName)) ||
      (header.includes('num') && header.includes(fieldName)) ||
      (header.includes('int') && header.includes(fieldName)) ||
      (header.includes('float') && header.includes(fieldName)) ||
      (header.includes('custom') && header.includes(fieldName)) ||
      normalizedHeader.includes(normalizedFieldName);
  }

  private matchesBooleanFieldPatterns(header: string, fieldName: string): boolean {
    const normalizedFieldName = fieldName.replace(/[\s\-_]/g, '').toLowerCase();
    const normalizedHeader = header.replace(/[\s\-_]/g, '').toLowerCase();

    return (header.includes('boolean') && header.includes(fieldName)) ||
      (header.includes('bool') && header.includes(fieldName)) ||
      (header.includes('flag') && header.includes(fieldName)) ||
      (header.includes('custom') && header.includes(fieldName)) ||
      normalizedHeader.includes(normalizedFieldName);
  }

  private matchesDateFieldPatterns(header: string, fieldName: string): boolean {
    const normalizedFieldName = fieldName.replace(/[\s\-_]/g, '').toLowerCase();
    const normalizedHeader = header.replace(/[\s\-_]/g, '').toLowerCase();

    return (header.includes('date') && header.includes(fieldName)) ||
      (header.includes('time') && header.includes(fieldName)) ||
      (header.includes('datetime') && header.includes(fieldName)) ||
      (header.includes('custom') && header.includes(fieldName)) ||
      normalizedHeader.includes(normalizedFieldName);
  }

  private applyDefaultQuantityIfNeeded(item: Partial<DocumentLineItemWithValidation>): void {
    if (!this.hasQuantityColumnMapped()) {
      item.quantity = this.importDefaultQuantityOneSettingSig() ? 1 : 0;
    }
  }

  private getGtinFromItem(item: DocumentLineItemWithValidation): string | null {
    if (!item.originalCsvRow) {
      return null;
    }

    const gtinColumnIndex = this.columnMappings().findIndex(mapping => mapping === 'gtin');
    if (gtinColumnIndex === -1) {
      return null;
    }

    return item.originalCsvRow[gtinColumnIndex]?.trim() || null;
  }

  private async validateSingleItemFallback(item: DocumentLineItemWithValidation, abortSignal: AbortSignal): Promise<void> {
    try {
      if (abortSignal.aborted) {
        return;
      }

      if (!item.productIdentifier && !this.getGtinFromItem(item)) {
        item.importIsValid.set(false);
        item.importError.set(this.i18n.translate(_('document-line-item.csv-import.product-identifier-or-gtin-required')));
        item.isValidating.set(false);
        return;
      }

      if (!this.hasImportableQuantity(item)) {
        item.importIsValid.set(false);
        item.importError.set(this.i18n.translate(_('document-line-item.csv-import.valid-quantity-required')));
        item.isValidating.set(false);
        return;
      }

      item.importIsValid.set(false);
      item.importError.set(this.t('validation-service-unavailable'));
      item.importProductExists.set(false);
    } catch (error) {
      if (!abortSignal.aborted) {
        console.error('Error validating item:', error);
        item.importIsValid.set(false);
        item.importError.set(this.t('validation-error'));
      }
    } finally {
      item.isValidating.set(false);
    }
  }

  private isReserveDocument(): boolean {
    return this.document?.documentDefinition?.stockAction === StockAction.RESERVE;
  }

  private hasImportableQuantity(item: DocumentLineItemWithValidation): boolean {
    if (!this.hasQuantityColumnMapped()) {
      return true;
    }
    const quantity = item.quantity ?? 0;
    const requestedQuantity = item.requestedQuantity ?? 0;
    if (this.isReserveDocument()) {
      return quantity > 0 || requestedQuantity > 0;
    }
    return quantity > 0;
  }

  private setCustomFieldValue(item: Partial<DocumentLineItemWithValidation>, fieldName: string, value: string): void {
    const customField = this.customFields().find(field => field.name === fieldName);
    if (!customField) {
      return;
    }

    const key = `customFields.${fieldName}`;
    switch (customField.type?.toLowerCase()) {
      case 'string':
        (item as any)[key] = value;
        break;
      case 'int':
      case 'number':
      case 'float':
        (item as any)[key] = xParseLocalizedNumber(value) || 0;
        break;
      case 'boolean':
        (item as any)[key] = value.toLowerCase() === 'true';
        break;
      case 'datetime':
        (item as any)[key] = new Date(value).toISOString();
        break;
      default:
        (item as any)[key] = value;
        break;
    }
  }

  private getCustomFieldValue(item: DocumentLineItemWithValidation, fieldName: string): string {
    const customField = this.customFields().find(field => field.name === fieldName);
    if (!customField) {
      return '';
    }

    const value = (item as any)[`customFields.${fieldName}`];
    if (value === undefined || value === null) {
      return '';
    }

    switch (customField.type) {
      case 'boolean':
        return value ? 'true' : 'false';
      case 'datetime':
        return value ? new Date(value as string).toISOString() : '';
      default:
        return String(value);
    }
  }
}
